import { useFlags } from "launchdarkly-react-client-sdk";

import {
  type LocalizationOwner,
  messageLocalizationOwner,
  ownerSettingsWhere,
} from "src/cep/messages/message/message-composer/localization/owner";
import type { LocalizationStampingState } from "src/cep/messages/message/message-composer/localization/stamp-decision";
import {
  type MessageLocalizationInput,
  useGetCepLocalizationSettingsQuery,
} from "src/graphql";
import type { ContentEditability } from "src/cep/messages/editability";
import { storesLocalizationOnMessage } from "src/cep/messages/message/message-composer/localization/localizable-channel";
import { ChannelType } from "src/types/cep";

export const LOCALIZED_CAMPAIGN_DRAFTS_BLOCKED_REASON =
  "Drafts aren't available for active localized campaigns yet";

type MessageLocalizationStampingArgs = {
  messageId: string | null;
  channelType: ChannelType | null;
  stagedLocalization?: MessageLocalizationInput | null;
};

type LocalizationStampingArgs = {
  owner: LocalizationOwner | null;
  channelType: ChannelType | null;
  stagedLocalization?: MessageLocalizationInput | null;
};

/** Only an email body carries tags a save could strip; an SMS body is tagged at render. */
const isStampedChannel = (channelType: ChannelType | null): boolean =>
  channelType === ChannelType.Email;

type MessageLocalizationSettingsRead = {
  enabled: boolean | null;
  isError: boolean;
};

function useLocalizationSettingsRead({
  owner,
  channelApplies,
  stagedLocalization,
}: Omit<LocalizationStampingArgs, "channelType"> & {
  channelApplies: boolean;
}): MessageLocalizationSettingsRead {
  const { cepLocalization } = useFlags();
  const applies = Boolean(cepLocalization) && channelApplies;
  const stagedSettings = stagedLocalization?.settings ?? null;
  const shouldReadPersistedSettings =
    applies && stagedSettings === null && owner !== null;

  const settingsQuery = useGetCepLocalizationSettingsQuery(
    { ownerFilter: ownerSettingsWhere(owner) },
    { enabled: shouldReadPersistedSettings },
  );

  if (!applies) return { enabled: false, isError: false };
  if (stagedSettings !== null) {
    return { enabled: stagedSettings.enabled, isError: false };
  }
  if (owner === null) return { enabled: false, isError: false };
  return {
    enabled:
      settingsQuery.data === undefined
        ? null
        : Boolean(settingsQuery.data.cep_localization_settings[0]?.enabled),
    isError: settingsQuery.isError,
  };
}

/** Null prevents an untagged save when persisted settings cannot be read. */
export function useLocalizationStampingState({
  owner,
  channelType,
  stagedLocalization,
}: LocalizationStampingArgs): LocalizationStampingState {
  const read = useLocalizationSettingsRead({
    owner,
    channelApplies: isStampedChannel(channelType),
    stagedLocalization,
  });
  return read.isError ? null : read.enabled;
}

export function useMessageLocalizationStampingState({
  messageId,
  channelType,
  stagedLocalization,
}: MessageLocalizationStampingArgs): LocalizationStampingState {
  return useLocalizationStampingState({
    owner: messageLocalizationOwner(messageId),
    channelType,
    stagedLocalization,
  });
}

/**
 * Whether per-message localization is switched on for this message. Gated on the
 * cepLocalization flag and the channels the Localize tab serves, and reads
 * false while the settings query resolves so a non-localized campaign never
 * flickers a gated control.
 */
export function useMessageLocalizationEnabled({
  messageId,
  channelType,
}: {
  messageId: string;
  channelType: ChannelType | null;
}): boolean {
  const read = useLocalizationSettingsRead({
    owner: messageLocalizationOwner(messageId),
    channelApplies: storesLocalizationOnMessage(channelType),
  });
  return read.enabled === true;
}

/**
 * Why a live campaign's content can't be drafted: its translations have no
 * draft lane. Undefined while the settings row loads, so a route can wait.
 */
export function useLocalizedLiveCampaignDraftBlock({
  message,
  content,
  isJourneyMessage,
}: {
  message: { id: string; channel_type: ChannelType | null };
  content: ContentEditability;
  isJourneyMessage: boolean;
}): string | null | undefined {
  return useLocalizedCampaignDraftBlock({
    messageId: message.id,
    channelType: message.channel_type,
    isLiveCampaign:
      content.canEdit && content.saveMode === "stage" && !isJourneyMessage,
  });
}

/** For callers that already know the campaign is live, without a surface to read. */
export function useLocalizedCampaignDraftBlock({
  messageId,
  channelType,
  isLiveCampaign,
}: {
  messageId: string;
  channelType: ChannelType | null;
  isLiveCampaign: boolean;
}): string | null | undefined {
  const read = useLocalizationSettingsRead({
    owner: isLiveCampaign ? messageLocalizationOwner(messageId) : null,
    channelApplies: storesLocalizationOnMessage(channelType),
  });
  if (!isLiveCampaign) return null;
  if (read.enabled === true) return LOCALIZED_CAMPAIGN_DRAFTS_BLOCKED_REASON;
  if (read.enabled === false) return null;
  // With no row read at all, blocking is safer than a draft that strands translations.
  return read.isError ? LOCALIZED_CAMPAIGN_DRAFTS_BLOCKED_REASON : undefined;
}
